import React, {useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import {TooltipDisableReason} from '@src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import CustomButton from '@src/components/shared/custom-button/custom-button';
import CustomIcon, {IconType} from '@src/components/shared/custom-icon/custom-icon';
import ThreeDotLoadingAnimation
    from '@src/components/features/version-button/saving-status-indication/three-dot-loading-animation/three-dot-animation';
import {isVersionPending} from '@src/utils/versionUtils';
import {useStores} from '@src/components/store';
import {BRONZE_MAINTENANCE_DISABLED_TOOLTIP_TEXT, VIEW_ONLY_DISABLED_TOOLTIP_TEXT} from '@src/utils/constants';
import {BronzeTabName} from '@src/dtos/analytics.dto';
import {BronzeVersionState} from '@src/utils/enums';
import './apply-bronze-button.scss';

const prefixIconMap: Partial<Record<BronzeVersionState, JSX.Element>> = {
    [BronzeVersionState.PENDING]: (
        <span className="pending-icon" data-testid="pending-icon">
            <CustomIcon type={IconType.STATUS_PENDING} />
        </span>
    ),
    [BronzeVersionState.APPLIED]: (
        <span className="saved-icon" data-testid="create-saved-icon">
            <CustomIcon type={IconType.CreateSavedStatus} />
        </span>
    ),
    [BronzeVersionState.SAVING]: (
        <span data-testid="create-saving-icon">
            <ThreeDotLoadingAnimation />
        </span>
    ),
};

const getPrefixIcon = (state: BronzeVersionState) => prefixIconMap[state];

export const ApplyBronzeButton: React.FC<{ standardApplyType?: BronzeTabName }> = observer(({standardApplyType}) => {
        const { customerStore, userStore, fwVersionBronzeStore } = useStores();
        const { versionState } = fwVersionBronzeStore;

        const disableReason = useMemo(() => {
            if (userStore.isReadOnlyUser) return VIEW_ONLY_DISABLED_TOOLTIP_TEXT;
            if (!customerStore.isAvailable) return BRONZE_MAINTENANCE_DISABLED_TOOLTIP_TEXT;
            return undefined;
        }, [userStore.isReadOnlyUser, customerStore.isAvailable]);

        const isDisabled =
            !fwVersionBronzeStore.isDirty ||
            isVersionPending(fwVersionBronzeStore.selectedFwVersion) ||
            !customerStore.isAvailable ||
            userStore.isReadOnlyUser;


        // Move toggle logic here
        const handleToggleVersionStateOnHover = () => {
            if (fwVersionBronzeStore.versionState === BronzeVersionState.APPLY) {
                fwVersionBronzeStore.setVersionState(BronzeVersionState.DRAFT);
            } else if (fwVersionBronzeStore.versionState === BronzeVersionState.DRAFT) {
                fwVersionBronzeStore.setVersionState(BronzeVersionState.APPLY);
            }
        };

        // Move apply logic here
        const handleApplyBronzePolicy = async () => {
            if (!fwVersionBronzeStore.selectedFwVersion) return;
            fwVersionBronzeStore.setApplyPolicyTime(performance.now());
            fwVersionBronzeStore.setVersionState(BronzeVersionState.SAVING);
            try {
                await fwVersionBronzeStore.applyPolicyBronze(standardApplyType);
            } catch {
                fwVersionBronzeStore.setVersionState(BronzeVersionState.DRAFT);
                fwVersionBronzeStore.displayErrorAndClear();
            }
        };

        if (!versionState) return null;

        return (
            <TooltipDisableReason
                overlayClassName="apply-bronze-policy-tooltip"
                disableReason={disableReason}
            >
                <CustomButton
                    className={`custom-btn primary-btn btn-apply ${versionState.toLowerCase()}`}
                    label={versionState === BronzeVersionState.SAVING ? '' : versionState}
                    isDisabled={isDisabled}
                    prefixIcon={getPrefixIcon(versionState)}
                    onClick={handleApplyBronzePolicy}
                    onMouseEnter={handleToggleVersionStateOnHover}
                    onMouseLeave={handleToggleVersionStateOnHover}
                />
            </TooltipDisableReason>
        );
    }
);
